import React, { useState, useEffect } from 'react';
import {
  Smartphone,
  Apple,
  Zap,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Layers,
  Cpu,
  WifiOff,
  Fingerprint,
  Star,
  Activity,
  Download,
  Code2,
  PenTool,
  Palette,
  Compass,
  ChevronLeft,
  ChevronRight,
  Search,
  Target,
  Server,
  Rocket,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

const MobileAppDevelopment = () => {
  const [activePlatform, setActivePlatform] = useState('react-native');
  const [currentSlide, setCurrentSlide] = useState(0);

  const mobileEngineSlides = [
    {
      id: 1,
      appName: 'HOAPS E-Commerce Store',
      badge: '60 FPS Native',
      badgeColor: 'text-cyan-300 bg-cyan-500/20 border-cyan-500/40',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80',
      metric: '4.9 ★ Rating',
      detail: 'SwiftUI & Apple Pay Checkout'
    },
    {
      id: 2,
      appName: 'Fintech Pay Vault',
      badge: 'AES-256 Vault',
      badgeColor: 'text-orange-300 bg-orange-500/20 border-orange-500/40',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80',
      metric: 'Biometric Security',
      detail: 'Face ID & Encrypted Keychain'
    },
    {
      id: 3,
      appName: 'NCD Patient Portal',
      badge: 'Real-Time Sync',
      badgeColor: 'text-emerald-300 bg-emerald-500/20 border-emerald-500/40',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
      metric: 'HealthKit Sync',
      detail: 'Vitals & Tele-Consult Video'
    },
    {
      id: 4,
      appName: 'AI Assistant Copilot',
      badge: '< 120ms Latency',
      badgeColor: 'text-purple-300 bg-purple-500/20 border-purple-500/40',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      metric: 'LLM Voice AI',
      detail: 'Conversational Native Chat'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mobileEngineSlides.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [mobileEngineSlides.length]);

  const mobilePillars = [
    {
      icon: Apple,
      title: 'iOS Swift Native Development',
      badge: 'Apple Ecosystem',
      color: 'from-blue-500 to-cyan-500',
      description: 'Bespoke native iOS apps built with SwiftUI, Combine, and Xcode. Engineered for maximum speed, Metal graphics, and Apple Watch / iPad sync.',
      highlights: ['SwiftUI & Combine Framework', 'Apple Pay & HealthKit Integration', 'TestFlight Beta Deployment']
    },
    {
      icon: Smartphone,
      title: 'Android Kotlin Native Apps',
      badge: 'Google Play',
      color: 'from-emerald-500 to-teal-600',
      description: 'Native Android applications leveraging Kotlin, Jetpack Compose, and Material You guidelines for high performance across 10,000+ Android devices.',
      highlights: ['Jetpack Compose UI Engine', 'Background Workers & Push Sync', 'Google Play Console Release']
    },
    {
      icon: Layers,
      title: 'Cross-Platform React Native & Flutter',
      badge: 'Single Codebase',
      color: 'from-purple-500 to-pink-600',
      description: 'Build native iOS and Android apps simultaneously with React Native or Flutter, cutting development costs by 40% while preserving 60 FPS performance.',
      highlights: ['Single Codebase Maintenance', '60 FPS Native UI Performance', 'Rapid Hot-Reload Iteration']
    },
    {
      icon: WifiOff,
      title: 'Offline-First & Progressive Web Apps (PWAs)',
      badge: 'Zero Friction',
      color: 'from-orange-500 to-amber-500',
      description: 'Web applications that behave like native apps with background service workers, offline storage, instant installation, and push alerts.',
      highlights: ['Local Storage & SQLite Caching', 'Background Sync & Push Alerts', 'Instant Home Screen Install']
    },
    {
      icon: Fingerprint,
      title: 'Biometric Security & AES-256 Encryption',
      badge: 'Bank Grade',
      color: 'from-rose-500 to-pink-600',
      description: 'Bank-grade mobile security enforcing Face ID, Touch ID, fingerprint authentication, SSL Certificate Pinning, and AES-256 encrypted local vaults.',
      highlights: ['Face ID / Touch ID Integration', 'SSL Certificate Pinning', 'Encrypted Local Keychain']
    },
    {
      icon: Zap,
      title: 'Mobile Micro-Interactions & Haptics',
      badge: 'Trendy UI/UX',
      color: 'from-cyan-500 to-blue-600',
      description: 'Fluid gesture animations, haptic feedback triggers, and smooth Lottie micro-animations that deliver a premium, modern mobile experience.',
      highlights: ['Custom Haptic Touch Feedback', 'Lottie Vector Animations', 'Fluid Swipe Gesture Navigation']
    }
  ];

  const designShowcases = [
    {
      title: 'Mobile UX Strategy & Wireframe Planning',
      subtitle: 'Mapping User Flows & Clickable Low-Fi Prototypes',
      description: 'Before writing code, our UX architects map user journey loops, tap targets, and navigation hierarchies to eliminate friction.',
      image: 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?w=800&q=80',
      badge: 'Planning Phase'
    },
    {
      title: 'Figma Mobile UI Design Systems & Theme Tokens',
      subtitle: 'Dark / Light Mode & Modular Component Libraries',
      description: 'We construct scalable Figma mobile component systems complete with typography scales, micro-animations, and dynamic theme tokens.',
      image: 'https://images.unsplash.com/photo-1616469829941-c7200edec809?w=800&q=80',
      badge: 'Design System'
    }
  ];

  const platformSpecs = {
    'react-native': {
      title: 'React Native & Expo Ecosystem',
      desc: 'Ideal for startups and scale-ups needing fast cross-platform deployment on iOS and Android with a shared JavaScript / TypeScript codebase.',
      specs: ['95%+ Code Sharing', 'Over-The-Air (OTA) Updates', 'Native Module Bridges', 'Fast Refresh DX']
    },
    'swift': {
      title: 'Native iOS (Swift & SwiftUI)',
      desc: 'Delivers maximum raw performance, deep Apple hardware access, CoreML AI models, and pixel-perfect Human Interface Guidelines compliance.',
      specs: ['SwiftUI Declarative UI', 'CoreML On-Device AI', 'Metal GPU Acceleration', 'Apple Watch / TV Extensions']
    },
    'kotlin': {
      title: 'Native Android (Kotlin & Jetpack)',
      desc: 'Engineered for extreme device diversity, background worker queues, custom hardware sensors, and Material Design 3 guidelines.',
      specs: ['Jetpack Compose Engine', 'Coroutines Asynchronous Execution', 'Material You Dynamic Themes', 'Android Auto Sync']
    }
  };

  const lifecycleSteps = [
    {
      step: '01',
      title: 'Discovery & Requirement Analysis',
      desc: 'Understand business goals, target audience, project scope, and technical requirements to define a clear roadmap.',
      icon: Search,
      badge: 'Ideation'
    },
    {
      step: '02',
      title: 'Product Strategy & Planning',
      desc: 'Plan features, user journeys, technology stack, milestones, timelines, and development strategy.',
      icon: Target,
      badge: 'Strategy'
    },
    {
      step: '03',
      title: 'UI/UX Design & Interactive Prototypes',
      desc: 'Create wireframes, interactive prototypes, intuitive user experiences, and modern mobile interfaces.',
      icon: Palette,
      badge: 'Design'
    },
    {
      step: '04',
      title: 'Mobile App Development',
      desc: 'Build secure, scalable native and cross-platform mobile applications using modern technologies and clean architecture.',
      icon: Code2,
      badge: 'Engineering'
    },
    {
      step: '05',
      title: 'Backend Development & API Integration',
      desc: 'Develop robust backend systems, databases, authentication, cloud infrastructure, third-party APIs, payment gateways, and real-time synchronization.',
      icon: Server,
      badge: 'Architecture'
    },
    {
      step: '06',
      title: 'Testing & Quality Assurance',
      desc: 'Perform functional, usability, compatibility, security, and performance testing across multiple devices to ensure application reliability.',
      icon: ShieldCheck,
      badge: 'Validation'
    },
    {
      step: '07',
      title: 'App Store Deployment',
      desc: 'Publish applications to Apple App Store and Google Play Store with store optimization, compliance, release management, and version control.',
      icon: Rocket,
      badge: 'Release'
    },
    {
      step: '08',
      title: 'Maintenance & Continuous Improvement',
      desc: 'Provide ongoing monitoring, updates, feature enhancements, security patches, performance optimization, analytics, and technical support.',
      icon: RefreshCw,
      badge: 'Scale'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20 text-gray-100">

      {/* Hero Showcase Section with Dark Mobile Technology Background Banner */}
      <section className="py-24 relative overflow-hidden border-b border-gray-800/80 bg-gray-950">
        {/* Background Image Layer */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/dark_mobile_app_hero_bg.png"
            alt="Mobile Technology Background Banner"
            className="w-full h-full object-cover filter brightness-90 contrast-110 opacity-75 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/85 to-gray-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-gray-950/80" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
                <Smartphone className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                  Next-Gen Mobile App Engineering
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Crafting High-Performance <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Mobile Applications</span>
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                We design and engineer native iOS, Android, and cross-platform React Native apps built for 60 FPS speed, intuitive gesture UI, and flawless App Store execution.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Start Mobile Project
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#mobile-features">
                  <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Explore Capabilities
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Interactive Mock Smartphone Display Frame with Live Screen Slider */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative w-80 h-[560px] bg-gray-900 border-4 border-gray-800 rounded-[44px] p-4 shadow-2xl shadow-cyan-500/10 flex flex-col justify-between overflow-hidden backdrop-blur-xl group">

                {/* Smartphone Speaker Notch */}
                <div className="w-32 h-4 bg-gray-800 rounded-full mx-auto mb-2.5 flex items-center justify-center">
                  <div className="w-3 h-1 bg-gray-700 rounded-full" />
                </div>

                {/* Animated Screen Slider Content Container */}
                <div className="relative flex-1 overflow-hidden rounded-3xl bg-gray-950 border border-gray-800 flex flex-col justify-between p-3.5">

                  {/* Top Slide Header & Navigation Controls */}
                  <div className="flex items-center justify-between z-10 mb-2">
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${mobileEngineSlides[currentSlide].badgeColor}`}>
                      {mobileEngineSlides[currentSlide].badge}
                    </span>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setCurrentSlide((prev) => (prev === 0 ? mobileEngineSlides.length - 1 : prev - 1))}
                        className="p-1 rounded-lg bg-gray-900 border border-gray-800 text-gray-300 hover:text-cyan-400"
                        title="Previous Screen"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setCurrentSlide((prev) => (prev + 1) % mobileEngineSlides.length)}
                        className="p-1 rounded-lg bg-gray-900 border border-gray-800 text-gray-300 hover:text-cyan-400"
                        title="Next Screen"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* 100% Visible High-Definition App Screen Mockup Container */}
                  <div className="relative h-56 rounded-2xl overflow-hidden border border-gray-800 shadow-md group-hover:scale-[1.02] transition-transform duration-500 my-1">
                    <img
                      src={mobileEngineSlides[currentSlide].image}
                      alt={mobileEngineSlides[currentSlide].appName}
                      className="w-full h-full object-cover transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-transparent" />

                    {/* Floating App Label Overlay */}
                    <div className="absolute bottom-2.5 left-2.5 right-2.5 bg-gray-900/90 backdrop-blur-md border border-gray-800 rounded-xl p-2 text-left">
                      <div className="text-sm font-bold text-white leading-tight">
                        {mobileEngineSlides[currentSlide].appName}
                      </div>
                      <div className="text-[11px] text-cyan-300 font-medium truncate">
                        {mobileEngineSlides[currentSlide].detail}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Metric Bar */}
                  <div className="bg-gray-900/90 border border-gray-800 p-2.5 rounded-xl flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                        <Star className="w-3.5 h-3.5 text-emerald-400" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-bold text-white">{mobileEngineSlides[currentSlide].metric}</div>
                        <div className="text-[9px] text-gray-400 font-mono">SCREEN {currentSlide + 1} OF 4</div>
                      </div>
                    </div>

                    <span className="text-[9px] font-bold text-orange-400 uppercase bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-md">
                      ACTIVE UI
                    </span>
                  </div>

                </div>

                {/* Slider Indicator Dots & Engine Label */}
                <div className="pt-3 border-t border-gray-800 flex items-center justify-between px-2">
                  <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">HOAPS MOBILE ENGINE v2.4</span>

                  <div className="flex items-center space-x-1.5">
                    {mobileEngineSlides.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${currentSlide === idx ? 'w-5 bg-cyan-400' : 'w-1.5 bg-gray-700 hover:bg-gray-500'
                          }`}
                        title={`Go to slide ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Mobile Design Theme & Planning Showcase */}
      <section className="py-24 bg-gray-900/60 border-b border-gray-800/80">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                Mobile UX Strategy & Theme System
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Design Systems & <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Wireframe Planning</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Visualizing early UX wireframing, sitemaps, and Figma design tokens before deploying production code.
            </p>
          </div>

          {/* 2-Card Visual Showcase */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {designShowcases.map((showcase, idx) => (
              <div key={idx} className="group bg-gray-900 border border-gray-800 hover:border-cyan-500/60 rounded-3xl overflow-hidden shadow-2xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={showcase.image}
                    alt={showcase.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/30 to-transparent" />
                  <div className="absolute top-4 left-4 bg-gray-950/90 border border-cyan-500/30 text-cyan-300 text-sm font-bold px-3.5 py-1.5 rounded-full">
                    {showcase.badge}
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                    {showcase.title}
                  </h3>
                  <div className="text-sm text-orange-400 font-semibold mb-4">{showcase.subtitle}</div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {showcase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Full-Width Prototype Showcase Box with White Background */}
          <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-white p-8 md:p-12 shadow-2xl">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7 space-y-4 text-left">
                <span className="text-xs font-bold text-cyan-600 uppercase tracking-widest bg-cyan-50 border border-cyan-200 px-3 py-1 rounded-full inline-block">
                  LIVE APP PROTOTYPE SHOWCASE
                </span>
                <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
                  Interactive Mobile App Prototypes & Micro-Animations
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  We deliver clickable Figma prototypes and Lottie micro-animations so stakeholders can test tap targets, gesture swipes, and transition physics on real devices before engineering.
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <div className="flex items-center space-x-2 bg-slate-50 border border-gray-200 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-800 shadow-sm">
                    <PenTool className="w-4 h-4 text-orange-500" />
                    <span>Figma Auto-Layout</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-slate-50 border border-gray-200 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-800 shadow-sm">
                    <Palette className="w-4 h-4 text-cyan-600" />
                    <span>Dark/Light Theme Tokens</span>
                  </div>
                </div>
              </div>

              <div className="md:col-span-5 relative h-64 md:h-72 rounded-2xl overflow-hidden border border-gray-200 shadow-md">
                <img
                  src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80"
                  alt="Live Mobile App Prototype"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Business Impact & Engineering Metrics Banner */}
      <section className="py-12 bg-white border-y border-gray-800/80 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-4 rounded-2xl bg-black border border-cyan-500/20 hover:border-cyan-500/40 transition-colors">
              <div className="text-3xl md:text-4xl font-extrabold text-cyan-400 mb-1">3.5x</div>
              <div className="text-xs md:text-sm text-gray-300 font-bold uppercase tracking-wider mb-0.5">Higher Conversion Rate</div>
              <div className="text-[11px] text-gray-400">Streamlined checkout & sub-second render</div>
            </div>
            <div className="p-4 rounded-2xl bg-black border border-orange-500/20 hover:border-orange-500/40 transition-colors">
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-1">99.99%</div>
              <div className="text-xs md:text-sm text-gray-300 font-bold uppercase tracking-wider mb-0.5">Enterprise Cloud SLA</div>
              <div className="text-[11px] text-gray-400">Zero downtime & resilient architecture</div>
            </div>
            <div className="p-4 rounded-2xl bg-black border border-blue-500/20 hover:border-blue-500/40 transition-colors">
              <div className="text-3xl md:text-4xl font-extrabold text-blue-400 mb-1">40%</div>
              <div className="text-xs md:text-sm text-gray-300 font-bold uppercase tracking-wider mb-0.5">Lower TCO & Dev Cost</div>
              <div className="text-[11px] text-gray-400">Single codebase React Native & Flutter</div>
            </div>
            <div className="p-4 rounded-2xl bg-black border border-emerald-500/20 hover:border-emerald-500/40 transition-colors">
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-400 mb-1">100%</div>
              <div className="text-xs md:text-sm text-gray-300 font-bold uppercase tracking-wider mb-0.5">Full IP & Code Control</div>
              <div className="text-[11px] text-gray-400">Complete git repository ownership</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Capabilities Grid */}
      <section id="mobile-features" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Trendy UI & Engineering Pillars
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Full-Spectrum <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Mobile Solutions</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Engineered with modern architecture patterns, fluid touch gestures, and bank-grade security for iOS and Android.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mobilePillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-cyan-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <pillar.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {pillar.highlights.map((highlight, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive Platform Stack Selector */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Select Your Preferred Mobile Tech Stack
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8">
              We help you evaluate cross-platform vs. native development to align with your time-to-market and budget requirements.
            </p>

            <div className="inline-flex p-1 bg-gray-900 border border-gray-800 rounded-xl space-x-2">
              <button
                onClick={() => setActivePlatform('react-native')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePlatform === 'react-native'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                React Native / Cross-Platform
              </button>
              <button
                onClick={() => setActivePlatform('swift')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePlatform === 'swift'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Native iOS (Swift)
              </button>
              <button
                onClick={() => setActivePlatform('kotlin')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePlatform === 'kotlin'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Native Android (Kotlin)
              </button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-2xl text-left">
            <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">
              {platformSpecs[activePlatform].title}
            </h3>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              {platformSpecs[activePlatform].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {platformSpecs[activePlatform].specs.map((spec, idx) => (
                <div key={idx} className="bg-slate-50 border border-gray-200 rounded-xl p-4 flex items-center space-x-3 shadow-xs">
                  <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                  <span className="text-sm font-bold text-gray-900">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 8-Step Enterprise Mobile App Development Lifecycle */}
      <section className="py-24 bg-slate-50 border-t border-b border-gray-200 relative text-left">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-amber-100 border border-amber-300 text-amber-900 rounded-full px-4 py-1.5 mb-4 shadow-sm">
              <span className="text-xs font-extrabold uppercase tracking-wider">HOASP Engineering Methodology</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight leading-tight">
              Mobile App Development Lifecycle
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              From idea validation to App Store launch and continuous support, we deliver secure, scalable, and high-performance mobile applications for startups, enterprises, and growing businesses.
            </p>
          </div>

          {/* 8-Step Card Grid with Enterprise Styling */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lifecycleSteps.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-200 rounded-3xl p-7 shadow-sm hover:shadow-2xl transition-all duration-300 relative flex flex-col justify-between group hover:border-amber-500/50 hover:-translate-y-1"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-2xl font-black text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1 rounded-xl font-mono shadow-xs">
                      {item.step}
                    </span>
                    <div className="p-3 bg-slate-100 border border-slate-200 rounded-2xl text-slate-800 group-hover:bg-slate-900 group-hover:text-amber-400 transition-colors">
                      <item.icon className="w-5 h-5" />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug group-hover:text-amber-700 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-xs text-gray-600 leading-relaxed mb-6">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full">
                    {item.badge}
                  </span>
                  <span className="text-[11px] font-mono text-gray-400">PHASE {item.step}/08</span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Full-Width Mobile CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 via-gray-950 to-slate-900 relative overflow-hidden border-t border-gray-800/80 w-full">
        <div className="container mx-auto px-6 relative z-10 max-w-4xl text-center">
          <div className="bg-white via-gray-800 to-slate-900 border border-cyan-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">

            <h2 className="text-3xl md:text-5xl font-extrabold text-black mb-4 leading-tight">
              Ready to Build<span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">iOS & Android Mobile App? </span>
            </h2>

            <p className="text-black text-base mb-8 max-w-2xl mx-auto">
              Consult with our mobile software engineers to plan your app architecture, UI/UX prototype, and store submission roadmap.
            </p>
            <a href="/contact">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg">
                Schedule Mobile App Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default MobileAppDevelopment;